/**
 * Bearer 토큰 저장소.
 *
 * 진입 시 브릿지 `getToken`(v1) 응답을 보관 → axios 요청 인터셉터가 Authorization 헤더로 주입.
 *
 * 토큰 갱신 경로 (401):
 *  - `useInitAuth` 가 `registerTokenFetcher` 로 브릿지 `getAuthToken`(v2 — Flutter 가
 *    만료 확인 후 재발급) 래퍼를 등록
 *  - axios 응답 인터셉터가 401 감지 시 `refreshTokenFromBridge` 호출 → 성공하면 원요청 1회 재시도
 */

let token: string | null = null

/** 외부(useInitAuth)가 등록하는 토큰 fetcher. 브릿지 callHandler('getAuthToken') 래퍼 */
type TokenFetcher = () => Promise<string | null>
let tokenFetcher: TokenFetcher | null = null

/** 진행 중인 갱신 Promise — 동시 다발 401 이 한 번의 브릿지 재발급을 공유(single-flight) */
let refreshPromise: Promise<boolean> | null = null

export const setToken = (value: string): void => {
  token = value
  window.dispatchEvent(new CustomEvent('dev:log', { detail: `token 저장됨: ${value}` }))
}

export const getToken = (): string | null => token

/** 토큰 fetcher 등록. null 전달 시 해제 (cleanup/테스트 용) */
export const registerTokenFetcher = (fetcher: TokenFetcher | null): void => {
  tokenFetcher = fetcher
}

/**
 * 등록된 fetcher 로 토큰을 재발급. 성공 시 setToken 후 true.
 *
 *  - 갱신이 이미 진행 중이면 그 Promise 를 그대로 반환 — 브릿지 중복 호출 방지
 *  - fetcher 미등록 / 반환값 null·'' / throw → false (토큰 미변경, 호출측은 원래 에러 전파)
 */
export const refreshTokenFromBridge = (): Promise<boolean> => {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    if (!tokenFetcher) return false
    try {
      const newToken = await tokenFetcher()
      if (newToken == null || newToken === '') return false
      setToken(newToken)
      return true
    } catch {
      return false
    }
  })().finally(() => {
    // 다음 만료 사이클에서 새 갱신이 가능하도록 해제
    refreshPromise = null
  })

  return refreshPromise
}
